import { useSubmitWebsiteUrlMutation } from '@dieterApi/scan/useSubmitWebsiteUrlMutation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { Button } from '@components/ui/Button/Button';
import SearchIcon from '@mui/icons-material/Search';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Compact (super-expert-mode) entry point for the embedded website scan. The
 * page-by-page flow exposes the scan via the synthetic URL step, which the
 * dense form skips — so this card gives super-expert users the same way to
 * kick off the scan. Once a scan has been started the card disappears (the
 * floating progress chip then reflects status) and detected services pre-fill
 * the service questions below.
 */
export function CompactUrlStep({ submittedUrl }: { submittedUrl: string | null }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [submit, { loading }] = useSubmitWebsiteUrlMutation();
  const [url, setUrl] = useState('');

  // A scan has already been started — nothing to offer here anymore.
  if (submittedUrl) return null;

  const trimmed = url.trim();
  const isDisabled = trimmed.length === 0 || loading;

  async function onScan() {
    if (trimmed.length === 0) return;
    const normalised = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      await submit({ variables: { input: { url: normalised } } });
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? t('components.block.quest.scan.url.error'), {
        variant: 'error',
        stack: e?.stack,
      });
    }
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-start gap-2">
        <SearchIcon className="text-primary mt-0.5" fontSize="small" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-800">{t('components.block.quest.scan.url.compact_title')}</p>
          <p className="mt-1 text-sm text-gray-600">{t('components.block.quest.scan.url.compact_description')}</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              inputMode="url"
              placeholder={t('components.block.quest.scan.url.placeholder')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onScan();
              }}
              className="text-sm border border-slate-300 rounded px-3 py-2 w-full sm:max-w-xs focus:outline-none focus:ring-1 focus:ring-primary-root focus:border-primary-root"
            />
            <Button disabled={isDisabled} spinner={loading} onClick={onScan}>
              {t('components.block.quest.scan.url.compact_submit')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
